/*
 * regikit-collection-facet.js
 *
 * Dynamic "filter by collection" facet for the /collections/all page.
 *
 * Shopify has no native "filter by collection" facet, so when a visitor picks a
 * collection we load that collection's product grid via the Section Rendering API
 * and swap it into the current /collections/all page WITHOUT navigating away.
 *
 * The visitor always stays on /collections/all. The chosen collection is mirrored
 * in the URL as ?rk_collection=<handle> for shareable/reloadable/back-button state.
 *
 * To keep the theme's native product filters (price, availability, sort, …) working
 * while scoped to the chosen collection, we install two narrowly-gated hooks instead
 * of forking the minified facets.js:
 *   1. window.fetch  — when a collection is active, Section-Rendering fetches aimed at
 *                      /collections/all are redirected to /collections/<handle>.
 *   2. history.pushState — re-injects ?rk_collection so facets.js URL updates keep it.
 *
 * Both hooks are no-ops unless we are on /collections/all AND a collection is selected,
 * and only ever touch URLs that already contain `section_id=` + `/collections/all`.
 */
(function () {
  'use strict';

  var PARAM = 'rk_collection';
  var ALL_PATH = '/collections/all';

  var selectedHandle = null;
  var initialized = false;
  var mobileBlockHtml = null;

  var origFetch = window.fetch ? window.fetch.bind(window) : null;
  var origPushState = window.history.pushState.bind(window.history);

  function onAllPage() {
    var p = window.location.pathname.replace(/\/+$/, '');
    return p.length >= ALL_PATH.length && p.slice(-ALL_PATH.length) === ALL_PATH;
  }

  function getParam() {
    return new URLSearchParams(window.location.search).get(PARAM) || null;
  }

  function sectionId() {
    var grid = document.getElementById('product-grid');
    if (grid && grid.dataset.id) return grid.dataset.id;
    var el = document.querySelector('regikit-collection-facet[data-section-id]');
    return el ? el.getAttribute('data-section-id') : null;
  }

  function allPath() {
    var el = document.querySelector('regikit-collection-facet[data-all-url]');
    var url = el ? el.getAttribute('data-all-url') : ALL_PATH;
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (e) {
      return ALL_PATH;
    }
  }

  // The collection path to load for a handle (locale/market prefix preserved).
  function pathFor(handle) {
    var base = allPath();
    if (!handle) return base;
    return base.replace(/all$/, '') + handle;
  }

  // Current native filter params in the URL, minus our own param and section_id.
  function nativeParams() {
    var sp = new URLSearchParams(window.location.search);
    sp.delete(PARAM);
    sp.delete('section_id');
    return sp;
  }

  function setLoading(on) {
    document.querySelectorAll('.rk-collection-facet-wrap').forEach(function (w) {
      w.classList.toggle('is-loading', on);
    });
    var grid = document.getElementById('ProductGridContainer');
    if (grid) {
      grid.classList.toggle('rk-collection-loading', on);
      if (on) grid.setAttribute('aria-busy', 'true');
      else grid.removeAttribute('aria-busy');
    }
  }

  function updateActiveLinks(handle) {
    var current = handle || '';
    document.querySelectorAll('.rk-collection-facet__link').forEach(function (a) {
      var isActive = (a.getAttribute('data-handle') || '') === current;
      a.classList.toggle('is-active', isActive);
      if (isActive) a.setAttribute('aria-current', 'page');
      else a.removeAttribute('aria-current');
    });
  }

  function reinjectMobileBlock() {
    if (!mobileBlockHtml) return;
    var wrap = document.getElementById('FacetsWrapperMobile');
    if (!wrap) return;
    if (wrap.querySelector('regikit-collection-facet')) return;
    wrap.insertAdjacentHTML('afterbegin', mobileBlockHtml);
  }

  // Swap the relevant pieces of a Section-Rendering response into the page.
  // Returns false when the response had no product grid (caller may fall back).
  function swapFromHtml(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');

    var newGrid = doc.getElementById('ProductGridContainer');
    var curGrid = document.getElementById('ProductGridContainer');
    if (!newGrid || !curGrid) return false;
    curGrid.innerHTML = newGrid.innerHTML;

    var newCount = doc.getElementById('ProductCountDesktop');
    if (newCount) {
      document.querySelectorAll('#ProductCountDesktop').forEach(function (el) {
        el.innerHTML = newCount.innerHTML;
      });
    }
    var newMobileCount = doc.querySelector('.mobile-facets__count');
    if (newMobileCount) {
      document.querySelectorAll('.mobile-facets__count').forEach(function (el) {
        el.innerHTML = newMobileCount.innerHTML;
      });
    }

    ['FacetFiltersForm', 'FacetFiltersFormMobile'].forEach(function (id) {
      var nf = doc.getElementById(id);
      var cf = document.getElementById(id);
      if (nf && cf) cf.innerHTML = nf.innerHTML;
    });

    // The mobile collection facet lives inside the swapped mobile form, so the
    // response (a non-/all collection) wiped it — put it back.
    reinjectMobileBlock();

    return true;
  }

  function buildFetchUrl(pathname) {
    var sid = sectionId();
    var sp = nativeParams();
    var qs = sp.toString();
    return pathname + '?section_id=' + encodeURIComponent(sid) + (qs ? '&' + qs : '');
  }

  function fetchAndSwap(pathname) {
    if (!origFetch) {
      window.location.href = pathname; // graceful: no fetch support
      return Promise.resolve();
    }
    setLoading(true);
    return origFetch(buildFetchUrl(pathname))
      .then(function (r) { return r.text(); })
      .then(function (html) {
        if (!swapFromHtml(html)) {
          // Fallback: section_id not valid for this collection — fetch full page.
          return origFetch(pathname).then(function (r) { return r.text(); }).then(function (full) {
            swapFromHtml(full);
          });
        }
      })
      .catch(function () {})
      .then(function () {
        setLoading(false);
        updateActiveLinks(selectedHandle);
      });
  }

  function updateUrl() {
    var sp = nativeParams();
    if (selectedHandle) sp.set(PARAM, selectedHandle);
    var qs = sp.toString();
    var newUrl = window.location.pathname + (qs ? '?' + qs : '');
    // Keep facets.js's popstate contract: it reads state.searchParams (native only).
    var native = nativeParams().toString();
    origPushState({ searchParams: native, rkCollection: selectedHandle }, '', newUrl);
  }

  function applyCollection(handle, opts) {
    opts = opts || {};
    selectedHandle = handle || null;
    updateActiveLinks(selectedHandle);
    if (opts.updateUrl) updateUrl();
    return fetchAndSwap(pathFor(selectedHandle)).then(function () {
      if (opts.scroll) {
        var grid = document.getElementById('ProductGridContainer');
        if (grid && grid.getBoundingClientRect().top < 0) {
          grid.scrollIntoView();
        }
      }
    });
  }

  function onClick(e) {
    var link = e.target.closest && e.target.closest('.rk-collection-facet__link');
    if (!link) return;
    if (!onAllPage()) return;
    if (e.defaultPrevented) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
    e.preventDefault();
    applyCollection(link.getAttribute('data-handle') || '', { updateUrl: true, scroll: true });
  }

  function onPopState() {
    if (!onAllPage()) return;
    var h = getParam();
    if (h === selectedHandle) return; // collection unchanged — let facets.js handle it
    selectedHandle = h;
    updateActiveLinks(selectedHandle);
    fetchAndSwap(pathFor(selectedHandle));
  }

  function installHooks() {
    if (origFetch) {
      window.fetch = function (input, init) {
        try {
          if (selectedHandle && onAllPage()) {
            var url = (typeof input === 'string') ? input : (input && input.url);
            if (url && url.indexOf('section_id=') !== -1 && url.indexOf(ALL_PATH) !== -1) {
              var redirected = url.replace(ALL_PATH, '/collections/' + selectedHandle);
              if (typeof input === 'string') return origFetch(redirected, init);
              return origFetch(new Request(redirected, input), init);
            }
          }
        } catch (err) { /* fall through to original */ }
        return origFetch(input, init);
      };
    }

    window.history.pushState = function (state, title, url) {
      try {
        if (selectedHandle && onAllPage() && url != null) {
          var u = new URL(url, window.location.origin);
          u.searchParams.set(PARAM, selectedHandle);
          url = u.pathname + (u.search || '') + (u.hash || '');
        }
      } catch (err) { /* fall through */ }
      return origPushState(state, title, url);
    };
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function init() {
    if (initialized) return;
    initialized = true;

    installHooks();
    document.addEventListener('click', onClick);
    window.addEventListener('popstate', onPopState, true);

    ready(function () {
      var mobileEl = document.querySelector('regikit-collection-facet[data-context="mobile"]');
      if (mobileEl) mobileBlockHtml = mobileEl.outerHTML;

      // facets.js replaces the mobile filter form on native filter changes, which
      // wipes our mobile collection block — re-inject it whenever that happens.
      var mobileForm = document.getElementById('FacetFiltersFormMobile');
      if (mobileForm && window.MutationObserver) {
        new MutationObserver(function () {
          if (!mobileForm.querySelector('regikit-collection-facet')) {
            reinjectMobileBlock();
            updateActiveLinks(selectedHandle);
          }
        }).observe(mobileForm, { childList: true });
      }

      selectedHandle = getParam();
      if (selectedHandle && onAllPage()) {
        updateActiveLinks(selectedHandle);
        // Server rendered the full /all grid; load the selected collection in place.
        fetchAndSwap(pathFor(selectedHandle));
      } else {
        updateActiveLinks(null);
      }
    });
  }

  if (window.customElements && !customElements.get('regikit-collection-facet')) {
    customElements.define('regikit-collection-facet', class extends HTMLElement {
      connectedCallback() { init(); }
    });
  } else {
    ready(init);
  }
})();
